import React, { useEffect, useState } from "react";
import { withRouter } from "../../../App";
import { connect, useDispatch } from "react-redux";
import { getLoggenInToken } from "../../../store/selectors/AuthSelectors";
import { Button, Modal, Spinner } from "react-bootstrap";
import { get } from "../../../services/api";
import Status from "../Common/Status";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { extendJobWithCandidates } from "../../../store/actions/Jobs/jobActions";
import { loginRequest } from "../../../authConfig";
import { useMsal } from "@azure/msal-react";

const ExtendRequirement = ({
  showExtendModal,
  setShowExtendModal,
  selectedRequirement,
  token,
  candidates
}) => {
  const [requirements, setRequirements] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [candidatesList, setCandidatesList] = useState([]);
  const [excludedCandidates, setExcludedCandidates] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const { instance, accounts } = useMsal();
  const dispatch = useDispatch();

  useEffect(() => {
    if (selectedRequirement?.jobID) {
      getRequirements();
      fetchCandidates();
    }
  }, [selectedRequirement]);

  const getRequirements = async () => {
    try {
      const res = await get(
        `/api/job/requirement/job/${selectedRequirement?.jobID}`,
        token
      );
      setRequirements(res);
    } catch (error) {
      console.error("Error fetching requirements:", error);
      toast.error("Failed to fetch requirements");
    }
  };

  const fetchCandidates = async () => {
    if (selectedRequirement?._id) {
      try {
        const response = await get(
          `/api/job/candidate/job/${selectedRequirement?._id}`,
          token
        );
        console.log("Fetched candidates:", response);
        setCandidatesList(response || []);
      } catch (error) {
        console.error("Error fetching candidates:", error);
        toast.error("Failed to fetch candidates");
      }
    }
  };

  // Modified to allow any date to be selected
  const getMinStartDate = () => {
    return null; // Return null to allow any date to be selected
  };

  const handleRemoveCandidate = (candidateId) => {
    setExcludedCandidates(prev => {
      const newSet = new Set(prev);
      if (newSet.has(candidateId)) {
        newSet.delete(candidateId);
      } else {
        newSet.add(candidateId);
      }
      return newSet;
    });
  };

  const handleExtend = async () => {
    if (isLoading) return;
    
    if (!startDate || !endDate) {
      toast.error("Please select both start and end dates.");
      return;
    }

    if (dayjs(endDate).isBefore(dayjs(startDate))) {
      toast.error("End date must be after or the same as the start date.");
      return;
    }

    if (requirements.length === 0) {
      toast.error("No requirement found to extend");
      return;
    }
    
    setIsLoading(true);
    
    try {
      const { _id, _iv, ...jobData } = requirements[0];
      
      // Filter out excluded candidates
      const includedCandidates = candidatesList.filter(
        candidate => !excludedCandidates.has(candidate._id)
      );
      
      const extendPayload = {
        ...jobData,
        quantity: selectedRequirement.quantity,
        jobStartDate: startDate,
        jobEndDate: endDate,
        candidates: 0,
        candidatesList: includedCandidates.map(c => ({
          candidateId: c?.candidateDetails?._id,
          startTime: c.startTime || "08:00"
        }))
      };
      
      if (accounts?.length > 0) {
        try {
          const accessToken = await instance.acquireTokenSilent({
            ...loginRequest,
            account: accounts[0],
          });
          extendPayload.accessToken = accessToken.accessToken;
        } catch (error) {
          console.error("Error getting access token:", error);
        }
      }
      const success = await dispatch(
        extendJobWithCandidates(
          extendPayload,
          selectedRequirement._id,
          handleModalClose,
          token
        )
      );
      
      if (!success) {
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error extending job:", error);
      toast.error("Failed to extend job: " + (error.message || "Unknown error"));
      setIsLoading(false);
    }
  };
  
  const handleModalClose = () => {
    setIsLoading(false);
    setShowExtendModal(false);
  };

  return (
    <Modal
      className="fade bd-example-modal-lg"
      show={showExtendModal}
      size="lg"
      onHide={() => !isLoading && handleModalClose()}
    >
      <Modal.Header>
        <Modal.Title>
          Extend Requirement ({selectedRequirement?.jobID})
        </Modal.Title>
        <Button
          variant=""
          className="btn-close"
          onClick={() => !isLoading && handleModalClose()}
          disabled={isLoading}
        ></Button>
      </Modal.Header>
      <Modal.Body>
        <h3>Available Requirements with Same Plan ID</h3>
        <div id="user-tbl_wrapper" className="dataTables_wrapper no-footer" style={{overflowX: "auto"}}>
          <table
            id="projects-tbl"
            className="table ItemsCheckboxSec dataTable no-footer mb-2 mb-sm-0 scrollable-content"
          >
            <thead>
              <tr>
                <th>Plan ID</th>
                <th>Company</th>
                <th>Job Title</th>
                <th>Quantity</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {requirements.map((item, index) => (
                <tr key={index}>
                  <td>
                    <div className="d-flex align-items-center">
                      <p className="mb-0 ms-2">{item?.jobID || "--"}</p>
                    </div>
                  </td>
                  <td>{item?.company}</td>
                  <td>{item?.jobTitle}</td>
                  <td>{item?.quantity}</td>
                  <td>{item.jobStartDate ? dayjs(item.jobStartDate).format("DD-MM-YYYY") : "--"}</td>
                  <td>{item.jobEndDate ? dayjs(item.jobEndDate).format("DD-MM-YYYY") : "--"}</td>
                  <td>
                    <Status status={item.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h3 className="mt-4">Extend Requirement</h3>
        <div className="d-flex gap-3">
          <div>
            <label>Start Date</label>
            <input
              type="date"
              className="form-control"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              min={getMinStartDate()}
              disabled={isLoading}
            />
          </div>
          <div>
            <label>End Date</label>
            <input
              type="date"
              className="form-control"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={startDate || getMinStartDate()}
              disabled={isLoading}
              placeholder="Select end date"
            />
          </div>
        </div>
        
        {candidatesList.length > 0 && (
          <div className="mt-4">
            <h5>Candidates to extend ({candidatesList.length})</h5>
            <div className="candidate-list" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {candidatesList.map((candidate, index) => {
                const isExcluded = excludedCandidates.has(candidate._id);
                return (
                  <div 
                    key={index} 
                    className="position-relative d-inline-block"
                    style={{
                      opacity: isExcluded ? 0.5 : 1,
                      transition: 'opacity 0.2s'
                    }}
                  >
                    <span className={`badge ${isExcluded ? 'bg-secondary' : 'bg-info'} me-1 p-2 pe-4`}>
                      {candidate.candidateDetails?.firstName || ''} {candidate.candidateDetails?.lastName || ''}
                      <button 
                        type="button" 
                        className="btn-close btn-close-white position-absolute top-0 end-0 m-1" 
                        style={{
                          fontSize: '0.6rem',
                          padding: '0.25rem',
                          lineHeight: '0.8',
                          opacity: 0.8
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveCandidate(candidate._id);
                        }}
                        aria-label="Remove candidate"
                      />
                    </span>
                  </div>
                );
              })}
            </div>
            <p className="text-info mt-2">
              {excludedCandidates.size > 0 
                ? `${candidatesList.length - excludedCandidates.size} of ${candidatesList.length} candidates selected for extension. Click the X to exclude a candidate.`
                : `All ${candidatesList.length} assigned candidates will be extended. Click the X to exclude a candidate.`}
            </p>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => !isLoading && handleModalClose()} disabled={isLoading}>
          Close
        </Button>
        <Button variant="primary" onClick={handleExtend} disabled={isLoading}>
          {isLoading ? (
            <>
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
              <span className="ms-2">Processing...</span>
            </>
          ) : (
            "Extend"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const mapStateToProps = (state) => {
  return {
    token: getLoggenInToken(state),
    candidates: state.candidates.candidates || [],
  };
};

export default withRouter(connect(mapStateToProps)(ExtendRequirement));
